import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, output } from '@angular/core';

import { CodingModel } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

/** Whether the chip offers a code or takes one away — the only thing that differs between the two. */
export type CodingChipAction = 'add' | 'remove';

const ACTION_CLASS: Record<CodingChipAction, string> = {
  add: 'border border-dashed border-neutral-300 hover:border-primary-300 hover:bg-primary-50',
  remove: 'hover:bg-error-50',
};

const BASE_CLASS =
  'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-sm transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-50 motion-reduce:transition-none';

/**
 * One ICD-10 coding as a chip: the code, its name, and the single act available on it. An `add` chip
 * is dashed because it offers something not yet taken; a `remove` chip is solid because it reports
 * something that is.
 *
 * `display: contents`, so a run of these wraps in the caller's own flex row rather than nesting.
 */
@Component({
  selector: 'guarantees-coding-chip',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe],
  host: { class: 'contents' },
  template: `
    @if (readOnly()) {
      <span [class]="chipClass()">
        <span [class]="codeClass()">{{ coding().code }}</span>
        <span class="min-w-0 truncate text-neutral-500">{{ coding().name }}</span>
      </span>
    } @else {
      <button
        [class]="chipClass()"
        type="button"
        [disabled]="disabled()"
        [matTooltip]="action() === 'remove' ? ('guaranteeV2.action.removeDiagnosis' | translate) : ''"
        [matTooltipDisabled]="action() === 'add'"
        (click)="picked.emit(coding())">
        @if (action() === 'add') {
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">add</mat-icon>
        }
        <span [class]="codeClass()">{{ coding().code }}</span>
        <span class="min-w-0 truncate text-neutral-500">{{ coding().name }}</span>
        @if (action() === 'remove') {
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">close</mat-icon>
        }
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodingChipComponent {
  coding = input.required<CodingModel>();
  action = input<CodingChipAction>('remove');
  disabled = input(false, { transform: booleanAttribute });
  /** Renders the coding as a label: the code is worth reading where the act on it is not offered. */
  readOnly = input(false, { transform: booleanAttribute });
  /** White ground, for a chip on a tinted panel where the default grey would not read as a chip. */
  light = input(false, { transform: booleanAttribute });

  picked = output<CodingModel>();

  protected readonly chipClass = computed(() => {
    const action = this.action();
    const ground = action === 'add' ? '' : this.light() ? 'bg-neutral-0' : 'bg-neutral-100';

    return `${BASE_CLASS} ${ACTION_CLASS[action]} ${ground}`;
  });

  protected readonly codeClass = computed(() => `shrink-0 font-mono font-semibold ${this.action() === 'add' ? 'text-neutral-600' : 'text-neutral-700'}`);
}
